/// <reference types="vite/types/importMeta.d.ts" />

import * as React from 'react'
import { RouterProvider } from 'react-router-dom'
import { router } from './router'
import '~/entrypoints/application.css'

import { KeyArgsFunction } from '@apollo/client/cache/inmemory/policies'
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { relayStylePagination } from '@apollo/client/utilities'
import invariant from 'tiny-invariant'
import { ViewerProvider, viewerQuery } from '~/auth/use-viewer'
import { Toaster } from '~/ui/toaster'
import { createApolloLink } from '../common/create-apollo-link'
import { getMetaContentMaybe } from '../common/get-meta-content'

if (import.meta.env.DEV) {
  // Adds messages only in a dev environment
  loadDevMessages()
  loadErrorMessages()
}

const csrfToken = getMetaContentMaybe('csrf-token')

const excludePaginationArgs: KeyArgsFunction = (args) => {
  if (!args) return false
  return Object.keys(args).filter((k) => !['first', 'after'].includes(k))
}

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        users: relayStylePagination(excludePaginationArgs),
      },
    },
    Playlist: {
      fields: {
        playlistTracks: {
          // Custom merge function for playlistTracks field
          merge(existing = [], incoming) {
            // If incoming is null or undefined, return existing
            if (!incoming) return existing

            // For complete replacement (as in refetch), just return incoming
            if (incoming.__typename === 'PlaylistTrackConnection' || Array.isArray(incoming)) {
              return incoming
            }

            // For individual items or partial updates, merge with existing
            return [...existing]
          },
        },
      },
    },
  },
})

const apolloClient = new ApolloClient({
  uri: '/graphql',
  link: createApolloLink(csrfToken),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

const metaTag = document.querySelector('meta[name=viewer-cache]')
invariant(metaTag, 'missing viewer-cache meta tag')

const viewerDataContent = metaTag.getAttribute('content')
invariant(viewerDataContent, 'missing viewer-cache meta tag content')

const userData = JSON.parse(viewerDataContent)

if (!userData.data) {
  throw new Error('Error preloading viewer')
} else {
  apolloClient.writeQuery({
    query: viewerQuery,
    data: userData.data,
    variables: {},
  })
}

export default function App() {
  return (
    <React.StrictMode>
      <ApolloProvider client={apolloClient}>
        <ViewerProvider>
          <RouterProvider router={router} />
        </ViewerProvider>
      </ApolloProvider>
      <Toaster />
    </React.StrictMode>
  )
}
